import { createRootRoute, createRoute, createRouter, Outlet, redirect } from '@tanstack/react-router'
import { lazy, Suspense } from 'react'
import { Layout } from '@/components/layout'
import { hasToken } from '@/lib/api/token'

// Auth guard helper - checks for token synchronously (doesn't wait for zustand rehydration)
function requireAuth(currentPath: string) {
	if (!hasToken()) {
		throw redirect({
			to: '/login',
			search: {
				redirect: currentPath,
			},
		})
	}
}

// Lazy-loaded components
const HomePage = lazy(() => import('./pages/home-page'))
const AboutPage = lazy(() => import('./pages/about-page'))
const PrivacyPolicyPage = lazy(() => import('./pages/privacy-policy-page'))
const TermsOfServicePage = lazy(() => import('./pages/terms-of-service-page'))
const SearchResultsPage = lazy(() => import('./pages/search-results-page'))
const ListingDetailPage = lazy(() => import('./pages/listing-detail-page'))
const RegionLandingPage = lazy(() => import('./pages/region-landing'))
const IndustryLandingPage = lazy(() => import('./pages/industry-landing'))
const CombinedLandingPage = lazy(() => import('./pages/combined-landing'))

// Auth pages
const LoginPage = lazy(() => import('./pages/login-page'))
const RegisterPage = lazy(() => import('./pages/register-page'))
const ForgotPasswordPage = lazy(() => import('./pages/forgot-password-page'))
const ProfilePage = lazy(() => import('./pages/profile-page'))
const SavedSearchesPage = lazy(() => import('./pages/saved-searches-page'))
const ShortlistPage = lazy(() => import('./pages/shortlist-page'))
const NotificationPreferencesPage = lazy(() => import('./pages/notification-preferences-page'))
const DeleteAccountPage = lazy(() => import('./pages/delete-account-page'))
const ChangeEmailPage = lazy(() => import('./pages/change-email-page'))
const ChangePasswordPage = lazy(() => import('./pages/change-password-page'))

// Discovery pages
const DiscoveryPage = lazy(() => import('./features/discovery/pages/DiscoveryPage'))

// Contact pages
const InquiriesPage = lazy(() => import('./features/contact/pages/InquiriesPage'))

// Compare page
const ComparePage = lazy(() => import('./pages/compare-page'))

// Broker pages
const BrokersPage = lazy(() => import('./pages/brokers-page'))
const BrokerProfilePage = lazy(() => import('./pages/broker-profile-page'))

// Lazy loading wrapper
function LazyPage({ children }: { children: React.ReactNode }) {
	return (
		<Suspense
			fallback={
				<div className="min-h-screen flex items-center justify-center">
					<div className="animate-pulse text-muted-foreground">Laddar...</div>
				</div>
			}
		>
			{children}
		</Suspense>
	)
}

// 1. Create root route with shared layout
const rootRoute = createRootRoute({
	component: () => (
		<Layout>
			<Outlet />
		</Layout>
	),
})

// 2. Create individual routes

// === Home ===
const indexRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/',
	component: () => (
		<LazyPage>
			<HomePage />
		</LazyPage>
	),
})

// === Static Pages ===
const aboutRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/about',
	component: () => (
		<LazyPage>
			<AboutPage />
		</LazyPage>
	),
})

const privacyRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/privacy',
	component: () => (
		<LazyPage>
			<PrivacyPolicyPage />
		</LazyPage>
	),
})

const termsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/terms',
	component: () => (
		<LazyPage>
			<TermsOfServicePage />
		</LazyPage>
	),
})

// === Search ===
const searchRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/search',
	component: () => (
		<LazyPage>
			<SearchResultsPage />
		</LazyPage>
	),
})

// === Region Routes ===
const regionRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/region/$regionSlug',
	component: () => (
		<LazyPage>
			<RegionLandingPage />
		</LazyPage>
	),
})

// Region + Industry combined
const regionIndustryRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/region/$regionSlug/industry/$categorySlug',
	component: () => (
		<LazyPage>
			<CombinedLandingPage />
		</LazyPage>
	),
})

// Region + Industry + Subcategory combined
const regionSubcategoryRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/region/$regionSlug/industry/$categorySlug/$subcategorySlug',
	component: () => (
		<LazyPage>
			<CombinedLandingPage />
		</LazyPage>
	),
})

// Region + Business detail
const regionBusinessRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/region/$regionSlug/listing/$slug',
	component: () => (
		<LazyPage>
			<ListingDetailPage />
		</LazyPage>
	),
})

// === Industry Routes ===
const industryRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/industry/$categorySlug',
	component: () => (
		<LazyPage>
			<IndustryLandingPage />
		</LazyPage>
	),
})

// Industry + Subcategory
const subcategoryRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/industry/$categorySlug/$subcategorySlug',
	component: () => (
		<LazyPage>
			<CombinedLandingPage />
		</LazyPage>
	),
})

// === Business Detail Route ===
const listingDetailRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/listing/$slug',
	component: () => (
		<LazyPage>
			<ListingDetailPage />
		</LazyPage>
	),
})

// === Auth Routes ===
const loginRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/login',
	validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
		redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
	}),
	component: () => (
		<LazyPage>
			<LoginPage />
		</LazyPage>
	),
})

const registerRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/register',
	component: () => (
		<LazyPage>
			<RegisterPage />
		</LazyPage>
	),
})

const forgotPasswordRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/forgot-password',
	component: () => (
		<LazyPage>
			<ForgotPasswordPage />
		</LazyPage>
	),
})

// === Protected Routes (require authentication) ===
const profileRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/account',
	beforeLoad: () => requireAuth('/account'),
	component: () => (
		<LazyPage>
			<ProfilePage />
		</LazyPage>
	),
})

const savedSearchesRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/account/saved-searches',
	beforeLoad: () => requireAuth('/account/saved-searches'),
	component: () => (
		<LazyPage>
			<SavedSearchesPage />
		</LazyPage>
	),
})

const shortlistRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/account/shortlist',
	beforeLoad: () => requireAuth('/account/shortlist'),
	component: () => (
		<LazyPage>
			<ShortlistPage />
		</LazyPage>
	),
})

const notificationPreferencesRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/account/notifications',
	beforeLoad: () => requireAuth('/account/notifications'),
	component: () => (
		<LazyPage>
			<NotificationPreferencesPage />
		</LazyPage>
	),
})

const changeEmailRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/account/change-email',
	beforeLoad: () => requireAuth('/account/change-email'),
	component: () => (
		<LazyPage>
			<ChangeEmailPage />
		</LazyPage>
	),
})

const deleteAccountRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/account/delete',
	beforeLoad: () => requireAuth('/account/delete'),
	component: () => (
		<LazyPage>
			<DeleteAccountPage />
		</LazyPage>
	),
})

const changePasswordRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/account/change-password',
	beforeLoad: () => requireAuth('/account/change-password'),
	component: () => (
		<LazyPage>
			<ChangePasswordPage />
		</LazyPage>
	),
})

// === Discovery Routes ===
const discoveryRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/discovery',
	component: () => (
		<LazyPage>
			<DiscoveryPage />
		</LazyPage>
	),
})

// === Swipe Route (Protected) ===
const SwipePage = lazy(() => import('./features/discovery/pages/SwipePage'))

const swipeRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/swipe',
	beforeLoad: () => requireAuth('/swipe'),
	component: () => (
		<LazyPage>
			<SwipePage />
		</LazyPage>
	),
})

// === Compare Route ===
const compareRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/compare',
	component: () => (
		<LazyPage>
			<ComparePage />
		</LazyPage>
	),
})

// === Broker Routes ===
const brokersRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/maklare',
	component: () => (
		<LazyPage>
			<BrokersPage />
		</LazyPage>
	),
})

const brokerProfileRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/maklare/$slug',
	component: () => (
		<LazyPage>
			<BrokerProfilePage />
		</LazyPage>
	),
})

// === Contact Routes (protected) ===
const inquiriesRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: '/account/inquiries',
	beforeLoad: () => requireAuth('/account/inquiries'),
	component: () => (
		<LazyPage>
			<InquiriesPage />
		</LazyPage>
	),
})

// 3. Build route tree
const routeTree = rootRoute.addChildren([
	indexRoute,
	aboutRoute,
	privacyRoute,
	termsRoute,
	searchRoute,
	// Region routes
	regionRoute,
	regionIndustryRoute,
	regionSubcategoryRoute,
	regionBusinessRoute,
	// Industry routes
	industryRoute,
	subcategoryRoute,
	// Business detail
	listingDetailRoute,
	// Auth routes
	loginRoute,
	registerRoute,
	forgotPasswordRoute,
	profileRoute,
	savedSearchesRoute,
	shortlistRoute,
	notificationPreferencesRoute,
	changeEmailRoute,
	changePasswordRoute,
	deleteAccountRoute,
	// Discovery
	discoveryRoute,
	swipeRoute,
	// Compare
	compareRoute,
	// Brokers
	brokersRoute,
	brokerProfileRoute,
	// Contact
	inquiriesRoute,
])

// 4. Create router instance
export const router = createRouter({
	routeTree,
	defaultPreload: 'intent',
})

// 5. Type declaration for type safety
declare module '@tanstack/react-router' {
	interface Register {
		router: typeof router
	}
}
